import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  unstable_usePrompt as usePrompt,
} from "react-router-dom";

import useUnload from "../../hooks/useUnload";
import useGetLocalePath from "../../hooks/useGetLocalePath";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useRegisterNewsletter from "../../hooks/useRegNewsletter";

import {
  setBlur,
  setContactReason,
  setContactZIndex,
  setShowContactDialog,
  setShowPrivacyDialog,
  setShowNewsletterRegisterDialog,
} from "../../redux/reducers/uiReducer";

import { toast } from "react-toastify";
import { DevTool } from "@hookform/devtools";
import ReCAPTCHA from "react-google-recaptcha";
import { yupResolver } from "@hookform/resolvers/yup";

import HelpToast from "../help-toast";
import NewsletterRegForm from "./newsletter-reg-form";
import { errorToast, successToast, warnToast } from "../toast";
import { getNewsletterRegSchema } from "../../schemas/newsletterSchema";

import {
  isEmpty,
  isEmptyObject,
  HELP_TOAST_COUNT,
  equalsIgnoringCase,
} from "../../utils/validators";
import { logDebug, logError, logWarn } from "../../utils/logger";

function NewsletterRegisterDialog() {
  const { t } = useTranslation();

  const dialogRef = useRef(null);
  const helpToastRef = useRef(null);
  const recaptchaRef = useRef(null);

  const [zIndex, setZIndex] = useState(101);
  const [isBlocked, setIsBlocked] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { getLocalePath } = useGetLocalePath();

  const dispatch = useDispatch();
  const { settings } = useSelector((state) => state.settings);
  const { csrf_token } = settings;

  const {
    data: dataRegisterNewsletter,
    error: errorRegisterNewsletter,
    loading: loadingRegisterNewsletter,
    registerNewsletter,
    clear: clearRegisterNewsletter,
  } = useRegisterNewsletter();

  const defaultValues = useMemo(() => {
    return {
      email1: "",
      email2: "",
      consent_privacy: false,
      consent_marketing: false,
    };
  }, []);

  const loadingForm = useMemo(() => {
    return [recaptchaLoading, loadingRegisterNewsletter].some((i) => i);
  }, [recaptchaLoading, loadingRegisterNewsletter]);

  const form = useForm({
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onChange",
    defaultValues: defaultValues,
    disabled: loadingRegisterNewsletter,
    resolver: yupResolver(getNewsletterRegSchema(t)),
  });
  const { control, formState, reset } = form;
  const { disabled, isDirty, submitCount } = formState;

  useUnload({
    when: isBlocked,
    message: t("message.askConfirmation"),
  });

  usePrompt({
    when: isBlocked,
    message: t("message.askConfirmation"),
  });

  useOnClickOutside(dialogRef, (evt) => {
    logDebug("NewsletterRegisterDialog", "useOnClickOutside", {
      evt: evt,
    });
    if (equalsIgnoringCase(evt.target.className, "wrapper")) {
      if (isBlocked) {
        if (window.confirm(t("message.askConfirmation"))) {
          closeDialogHandler(evt);
        }
      } else {
        closeDialogHandler(evt);
      }
    }
  });

  const closeDialogHandler = useCallback(
    (evt) => {
      logDebug("NewsletterRegisterDialog", "closeDialogHandler", {
        evt: evt,
      });
      setIsBlocked(false);
      setTimeout(() => {
        if (
          equalsIgnoringCase(
            getLocalePath(t("routes.public.newsletter_reg")),
            location.pathname
          )
        ) {
          navigate(getLocalePath(t("routes.common.home")), {
            state: { targetId: "" },
          });
        }
      }, 100);
      setTimeout(() => {
        dispatch(setBlur(false));
        dispatch(setShowNewsletterRegisterDialog(false));
      }, 200);
    },
    [dispatch, getLocalePath, location.pathname, navigate, t]
  );

  const notifyHelpToast = useCallback(() => {
    logDebug("NewsletterRegisterDialog", "notifyHelpToast", {});
    helpToastRef.current = toast(<HelpToast />, {
      toastId: "need_help",
      autoClose: false,
      closeButton: true,
      closeOnClick: false,
      position: "bottom-right",
      className: "help-toast__container",
      data: {
        message: t("message.need_help"),
        onContact: (evt) => {
          logDebug("HelpMessage", "onContact", { evt: evt });
          dispatch(setContactReason("4"));
          dispatch(setContactZIndex(zIndex + 3));
          dispatch(setBlur(true));
          dispatch(setShowContactDialog(true));
        },
      },
    });
  }, [zIndex, dispatch, t]);

  const onInvalidHandler = useCallback(
    (errors, evt) => {
      const handler = async (errors, evt) => {
        logError("NewsletterRegisterDialog", "onInvalidHandler", {
          errors: errors,
          evt: evt,
        });
        errorToast(
          "NewsletterRegisterDialog",
          errors,
          t("errors.error_form", t("errors.default"))
        );
        if (submitCount >= HELP_TOAST_COUNT) notifyHelpToast();
      };
      handler(errors, evt);
    },
    [submitCount, notifyHelpToast, t]
  );

  const onValidHandler = useCallback(
    (data, evt) => {
      const handler = async (data, evt) => {
        if (!disabled) {
          logDebug("NewsletterRegisterDialog", "onValidHandler", {
            data: data,
            evt: evt,
          });
          if (!isEmptyObject(data)) {
            setRecaptchaLoading(true);
            const recaptcha = await recaptchaRef.current.executeAsync();
            setRecaptchaLoading(false);
            if (!isEmpty(recaptcha)) {
              registerNewsletter({
                ...data,
                csrf_token: !isEmpty(csrf_token) ? csrf_token : "",
                "g-recaptcha-response": recaptcha,
              });
            } else {
              logError("NewsletterRegisterDialog", "onValidHandler", {
                error: "invalid_recaptcha",
                msg: t("validators.invalid_recaptcha", t("errors.default")),
              });
              errorToast(
                "NewsletterRegisterDialog - onValidHandler",
                "invalid_recaptcha",
                t("validators.invalid_recaptcha", t("errors.default"))
              );
              recaptchaRef.current.reset();
            }
          }
        }
      };
      handler(data, evt);
    },
    [csrf_token, disabled, t, registerNewsletter]
  );

  const onConsentClickHandler = useCallback(
    (evt, type) => {
      logDebug("NewsletterRegisterDialog", "onConsentClickHandler", {
        evt: evt,
        type: type,
      });
      evt.preventDefault();
      evt.stopPropagation();
      if (equalsIgnoringCase(type, "PRIVACY")) {
        dispatch(setBlur(true));
        dispatch(setShowPrivacyDialog(true));
      }
    },
    [dispatch]
  );

  const onErroredReCaptchaHandler = useCallback(
    (evt) => {
      logError("NewsletterRegisterDialog", "onErroredReCaptchaHandler", {
        evt: evt,
        code: "error_recaptcha",
        msg: t(`errors.error_recaptcha`, t("errors.default")),
      });
      errorToast(
        "NewsletterRegisterDialog",
        "error_recaptcha",
        t(`errors.error_recaptcha`, t("errors.default"))
      );
      setRecaptchaLoading(false);
      recaptchaRef.current.reset();
    },
    [t]
  );

  const onExpiredReCaptchaHandler = useCallback(
    (evt) => {
      logWarn("NewsletterRegisterDialog", "onExpiredReCaptchaHandler", {
        evt: evt,
        code: "expired_recaptcha",
        msg: t(`validators.expired_recaptcha`, t("errors.default")),
      });
      warnToast(
        "NewsletterRegisterDialog",
        "expired_recaptcha",
        t(`validators.expired_recaptcha`, t("errors.default"))
      );
      setRecaptchaLoading(false);
      recaptchaRef.current.reset();
    },
    [t]
  );

  const asyncScriptOnLoadReCaptchaHandler = useCallback((evt) => {
    logDebug("NewsletterRegisterDialog", "asyncScriptOnLoadReCaptchaHandler", {
      evt: evt,
    });
  }, []);

  useEffect(() => {
    setIsBlocked(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (!isEmptyObject(dialogRef) && !isEmptyObject(dialogRef.current)) {
      const elem = dialogRef.current;
      if (!isEmptyObject(elem)) {
        const compStyles = window.getComputedStyle(elem);
        if (!isEmptyObject(compStyles)) {
          const zIndex = compStyles.getPropertyValue("z-index");
          if (!isEmpty(zIndex)) {
            setZIndex(parseInt(zIndex));
          }
        }
      }
    }
    return () => {
      if (
        !isEmptyObject(helpToastRef) &&
        !isEmptyObject(helpToastRef.current)
      ) {
        toast.dismiss(helpToastRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!loadingRegisterNewsletter) {
      if (!isEmpty(errorRegisterNewsletter)) {
        logError("NewsletterRegisterDialog", "registerNewsletter", {
          error: errorRegisterNewsletter,
          msg: t(`errors.${errorRegisterNewsletter}`, t("errors.default")),
        });
        errorToast(
          "NewsletterRegisterDialog - registerNewsletter",
          errorRegisterNewsletter,
          t(`errors.${errorRegisterNewsletter}`, t("errors.default"))
        );
        clearRegisterNewsletter();
        recaptchaRef.current.reset();
        if (submitCount >= HELP_TOAST_COUNT) notifyHelpToast();
      } else if (!isEmptyObject(dataRegisterNewsletter)) {
        logDebug("NewsletterRegisterDialog", "registerNewsletter", {
          data: dataRegisterNewsletter,
          msg: t(
            `message.${dataRegisterNewsletter.result}`,
            t("message.default")
          ),
        });
        successToast(
          "NewsletterRegisterDialog - registerNewsletter",
          dataRegisterNewsletter,
          t(`message.${dataRegisterNewsletter.result}`, t("message.default"))
        );
        reset(defaultValues);
        recaptchaRef.current.reset();
        setIsBlocked(false);
        setTimeout(() => {
          dispatch(setBlur(false));
          dispatch(setShowNewsletterRegisterDialog(false));
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataRegisterNewsletter,
    errorRegisterNewsletter,
    loadingRegisterNewsletter,
  ]);

  return (
    <>
      <div className="wrapper"></div>
      <div ref={dialogRef} className="newsletter__dialog">
        <div className="newsletter__dialog__header">
          <span className="newsletter__dialog__header__logo"></span>
          <div className="newsletter__dialog__header__title header-3">
            {t("reg_newsletter_dialog.title")}
          </div>
          <div className="newsletter__dialog__header__subtitle text-body">
            {t("reg_newsletter_dialog.subtitle")}
          </div>
        </div>
        <div className="newsletter__dialog__body">
          <NewsletterRegForm
            form={form}
            loading={loadingForm}
            onValid={(data, evt) => onValidHandler(data, evt)}
            onInvalid={(errors, evt) => onInvalidHandler(errors, evt)}
            onConsentClick={(evt, type) => onConsentClickHandler(evt, type)}
          />
        </div>
        <div className="newsletter__dialog__footer"></div>
      </div>
      <ReCAPTCHA
        ref={recaptchaRef}
        className="newsletter-reCAPTCHA"
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
        badge="bottomright"
        size="invisible"
        onErrored={(evt) => onErroredReCaptchaHandler(evt)}
        onExpired={(evt) => onExpiredReCaptchaHandler(evt)}
        asyncScriptOnLoad={(evt) => asyncScriptOnLoadReCaptchaHandler(evt)}
      />
      <DevTool id="newsletter-register-dialog__devtools" control={control} />
    </>
  );
}

export default NewsletterRegisterDialog;
